import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PrivacyConsentProps {
    onConsentChange: (field: string, value: boolean) => void;
    required?: boolean;
}

export default function PrivacyConsent({ onConsentChange, required = true }: PrivacyConsentProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="space-y-4 text-sm">
            <div className="bg-gray-50 p-4 rounded-md text-gray-600">
                <p className="mb-3">
                    EmploSkills is committed to protecting and respecting your privacy. 
                    {!isExpanded && (
                        <button 
                            onClick={() => setIsExpanded(true)}
                            className="text-primary hover:text-primary-dark ml-1 inline-flex items-center"
                        >
                            Read more <ChevronDown className="w-4 h-4 ml-1" />
                        </button>
                    )}
                </p>
                
                {isExpanded && (
                    <>
                        <p className="mb-3">
                            We'll only use your personal information to administer your account and to provide the products 
                            and services you requested from us. From time to time, we would like to contact you about our 
                            products and services, as well as other content that may be of interest to you.
                        </p>
                        <button 
                            onClick={() => setIsExpanded(false)}
                            className="text-primary hover:text-primary-dark mb-3 inline-flex items-center"
                        >
                            Show less <ChevronUp className="w-4 h-4 ml-1" />
                        </button>
                    </>
                )}

                <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                        type="checkbox"
                        name="marketingConsent"
                        value="true"
                        required={required}
                        onChange={(e) => onConsentChange('marketingConsent', e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary group-hover:border-primary"
                    />
                    <span>
                        I agree to receive communications from EmploSkills
                        {required && <span className="text-red-500">*</span>}
                    </span>
                </label>

                {isExpanded && (
                    <>
                        <p className="mt-4 text-sm text-gray-500">
                            You can unsubscribe from these communications at any time. For more information on how to unsubscribe,
                            our privacy practices, and how we are committed to protecting and respecting your privacy, please review
                            our <Link to="/privacy-policy" className="text-primary hover:text-primary-dark underline">Privacy Policy</Link>
                            {' '}and{' '}
                            <Link to="/cookie-policy" className="text-primary hover:text-primary-dark underline">Cookie Policy</Link>.
                        </p>
                        <p className="mt-4 text-sm text-gray-500">
                            By clicking submit below, you consent to allow EmploSkills to store and process the personal information
                            submitted above to provide you the content requested. We collect and process your data in accordance with
                            GDPR, CCPA, and other privacy laws.
                        </p>
                        <p className="mt-2 text-xs text-gray-400">
                            Note: This consent is separate from cookie tracking consent. Website tracking cookies are managed through
                            our cookie banner and can be controlled via Cookie Settings in the footer.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
} 